// The account page (ACCOUNTS.md, scenario A2): everything the site holds
// about a person, all of it changeable except the id. Display name, avatar
// and time zone, the account's achievements (ACHIEVEMENTS.md §11, R4), the
// email and password used to sign in, the devices signed in, a JSON export,
// and deletion behind a typed confirmation.
//
// Runs on the REAL session as of 2026-09-14 (ICD-S03). The name and avatar
// save through `PUT /api/me/profile`, the device list is `GET /api/me/sessions`
// rather than an invented row, revoking one is a real DELETE, and closing the
// account is `DELETE /api/me`.
//
// **Changing the email** is `PUT /api/me/email` since 2026-10-28 (B-548):
// behind the current password, and nothing changes until the link sent to the
// new address is used; a change waiting on it is said here, with Cancel.
// **Changing the password** is `PUT /api/me/password` since 2026-10-02
// (B-244); until 2026-09-14 the button showed "Password changed" and changed
// nothing.

import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { avatarColor, avatarHex, randomSeed, useSession, type Session } from '../data/session';
import { FaceMaker, type StartingFace } from './shared/FaceMaker';
import { revokeDevice, useDevices } from '../data/devices';
import { useWatchlist } from '../data/watchlist';
import { useAccountPrefs } from '../data/accountPrefs';
import { useAchievements } from '../data/achievements';
import { useAtLeast } from '../data/detail';
import { useLeagueCards } from '../data/LeaguesProvider';
import { messageOf } from '../data/api';
import { valueOf } from '@fsp/domain';
import { useDelight } from '../delight/useDelight';
import { PASSWORD_RULE } from './shared/passwordRule';
import { Hint } from '../kit/overlays';
import {
  AvatarMark,
  Button,
  Callout,
  Caption,
  Card,
  Line,
  Stack,
  Text,
  TextField,
  Title,
  Toggle,
  tell,
  tellWithUndo,
} from '../kit/ui';
import { FormScreen, type FormSection } from '../templates/FormScreen';
import { ChangePasswordSheet } from './shared/ChangePasswordSheet';
import { DeleteAccountSheet } from './shared/DeleteAccountSheet';
import { ChangeEmailSheet } from './shared/ChangeEmailSheet';
import { TimeZoneSelect } from './shared/TimeZoneSelect';
import { PositionPill, ProTeamCode } from '../kit';
import { FoundTile, OpenTile } from './shared/AchievementKit';

type SectionId = 'profile' | 'achievements' | 'chat' | 'signin' | 'devices' | 'watching' | 'data';

const SECTIONS: { id: SectionId; title: string }[] = [
  { id: 'profile', title: 'Profile' },
  // Second after Profile (ACHIEVEMENTS.md §11).
  { id: 'achievements', title: 'Achievements' },
  { id: 'chat', title: 'Chat' },
  { id: 'signin', title: 'Sign-in' },
  { id: 'devices', title: 'Devices' },
  { id: 'watching', title: 'Watching' },
  { id: 'data', title: 'Your data' },
];

// `deviceLabel()` lived here and guessed at the browser this page was
// rendered in, because the device list was a single invented row. The
// server labels each session at sign-in now (`deviceOf` in
// functions/api/_lib/session.ts), so the guess is gone: a label derived
// here could only ever describe THIS device, and the list has others.

function since(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function Avatar({
  session,
  size,
}: {
  session: Pick<Session, 'displayName' | 'avatarSeed' | 'avatarFace'>;
  size: 'md' | 'xl';
}) {
  return (
    <AvatarMark
      name={session.displayName}
      color={avatarColor(session.avatarSeed)}
      face={session.avatarFace}
      size={size}
    />
  );
}

export function AccountScreen() {
  const { session, update, signOut, cancelEmailChange } = useSession();
  const { play } = useDelight();
  const navigate = useNavigate();
  // `?section=` opens one: a watched player's notice opens the watchlist (E5),
  // and a notice tapped while this page is already open moves it there too.
  const [params] = useSearchParams();
  const asked = SECTIONS.find((s) => s.id === params.get('section'))?.id;
  const [active, setActive] = useState<SectionId>(asked ?? 'profile');
  useEffect(() => {
    if (asked !== undefined) setActive(asked);
  }, [asked]);
  const [draft, setDraft] = useState(() => ({
    displayName: session?.displayName ?? '',
    avatarSeed: session?.avatarSeed ?? 0,
    timeZone: session?.timeZone ?? null,
  }));
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // A FACE FOR THE PERSON (PLAN-2026-10-24-faces-round-three §7): the face
  // maker, as a team's, its Done keeping the face on the account at once.
  const [making, setMaking] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const devices = useDevices(session?.userId ?? null);
  const watchlist = useWatchlist({ fresh: true });
  const { account: prefs, saveAchievements, saveChat } = useAccountPrefs();
  const achievements = useAchievements();
  const wall = valueOf(achievements.snapshot);
  const counts = useAtLeast('standard');
  // The export reads the REAL leagues. It listed the simulator's example
  // ones until 2026-09-17 — a file called "everything we keep about you",
  // built from a world that exists in this browser and nowhere else, while
  // the leagues somebody actually joined were missing from it.
  const { cards } = useLeagueCards();
  // A failed refresh keeps the list it had; a list we never got is null, and
  // the section says so rather than claiming nobody is signed in anywhere.
  const rows = valueOf(devices.snapshot);

  if (!session) return null;

  const nameOk = draft.displayName.trim().length >= 2;
  const changes = [
    draft.displayName.trim() !== session.displayName,
    draft.avatarSeed !== session.avatarSeed,
    draft.timeZone !== session.timeZone,
  ].filter(Boolean).length;

  const sections: FormSection[] = SECTIONS.map((s) => ({
    ...s,
    errors: s.id === 'profile' && !nameOk ? 1 : 0,
    warnings: 0,
  }));

  const save = () => {
    setSaving(true);
    void (async () => {
      const outcome = await update({
        displayName: draft.displayName.trim(),
        // A NEW COLOR ONLY WHEN ONE WAS CHOSEN: a color is the initial again,
        // and a rename must not take off the face the account wears (B-498).
        ...(draft.avatarSeed !== session.avatarSeed ? { avatarSeed: draft.avatarSeed } : {}),
        ...(draft.timeZone !== null && draft.timeZone !== session.timeZone
          ? { timeZone: draft.timeZone }
          : {}),
      });
      setSaving(false);
      if (!outcome.ok) {
        // The save is reported by its actual result now. A screen that says
        // "Saved" before the server has agreed is how somebody loses a change
        // and never finds out.
        tell(outcome.message, 'bad');
        return;
      }
      play('lock');
      tell('Saved. Same account, same leagues.');
    })();
  };

  const discard = () =>
    setDraft({
      displayName: session.displayName,
      avatarSeed: session.avatarSeed,
      timeZone: session.timeZone,
    });

  const exportData = () => {
    const mine = cards.map((c) => ({
      leagueId: c.league.id,
      league: c.league.name,
      team: c.myTeam?.name ?? null,
      commissioner: c.league.isCommissioner,
    }));
    const data = {
      exportedAt: new Date().toISOString(),
      user: {
        id: session.userId,
        displayName: session.displayName,
        avatar: session.avatarFace
          ? { kind: 'face', face: session.avatarFace.face, colors: session.avatarFace.colors }
          : { kind: 'generated', seed: String(session.avatarSeed) },
        timeZone: session.timeZone,
      },
      logins: [{ kind: 'password', identifier: session.email }],
      // The real list, not this device guessed at. Empty if it has not
      // loaded — an export that invents a row is worse than one that is
      // honestly short.
      sessions: (rows ?? []).map((d) => ({
        device: d.device ?? 'Unknown device',
        since: d.createdAt,
        current: d.current,
      })),
      leagues: mine,
      // The account's choices and stars, as this page holds them.
      // TODO(A6): the activity log joins the download with `GET /api/me/export`.
      preferences: prefs,
      watchlist: (watchlist.players ?? []).map((w) => ({
        sport: w.sport,
        playerId: w.player.id,
        player: w.player.name,
        since: w.addedAt,
      })),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my-data-${session.userId.slice(0, 8)}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const help = (text: string) => (
    <Text size="xs" tone="secondary" block>
      {text}
    </Text>
  );

  let body: ReactNode;
  switch (active) {
    case 'profile':
      body = (
        <Stack gap={4}>
          <Line gap={4}>
            <Avatar session={{ ...draft, avatarFace: session.avatarFace }} size="xl" />
            <Stack gap={1}>
              <Text weight="strong" size="md">
                Your mark
              </Text>
              {help(
                session.avatarFace
                  ? 'A face you made, the same on every device.'
                  : 'Your initial on a color, the same on every device, or a face you make.',
              )}
              <Line gap={2} wrap>
                {session.avatarFace ? (
                  <>
                    <Button size="xs" icon="face" onClick={() => setMaking(true)}>
                      Edit face
                    </Button>
                    <Button
                      size="xs"
                      variant="quiet"
                      onClick={() => {
                        void update({ avatarFace: null }).then((o) =>
                          tell(
                            o.ok ? 'Your initial is your mark again.' : o.message,
                            o.ok ? 'good' : 'bad',
                          ),
                        );
                      }}
                    >
                      Use my initial
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="xs" icon="face" onClick={() => setMaking(true)}>
                      Make a face
                    </Button>
                    <Button
                      size="xs"
                      variant="quiet"
                      icon="refresh"
                      onClick={() => setDraft((d) => ({ ...d, avatarSeed: randomSeed() }))}
                    >
                      New color
                    </Button>
                  </>
                )}
              </Line>
            </Stack>
          </Line>
          <TextField
            label="What should we call you?"
            description="What other managers see. Not unique; two Kens are fine."
            value={draft.displayName}
            onChange={(v) => setDraft({ ...draft, displayName: v })}
            error={nameOk ? null : 'Give us at least two letters.'}
            maxLength={40}
          />
          <TimeZoneSelect
            value={draft.timeZone}
            onChange={(z) => setDraft({ ...draft, timeZone: z })}
            description={
              session.timeZone === null
                ? 'Not set yet, so a league shows its own zone and everything else shows this device’s. Pick yours and every time on the site follows it.'
                : 'Every time on the site is shown in this zone, wherever you are.'
            }
          />
        </Stack>
      );
      break;
    case 'achievements': {
      // THE THREE NEWEST, how many, the two open ones closest to done (at
      // Standard and above), Tell my leagues, and the wall (§11).
      const found = (wall?.families ?? [])
        .flatMap((f) => f.found)
        .sort((a, b) => b.earnedAt.localeCompare(a.earnedAt))
        .slice(0, 3);
      const closest = (wall?.families ?? [])
        .flatMap((f) => f.open.map((a) => ({ a, family: f.id })))
        .filter((x) => x.a.progress !== null && x.a.progress.have > 0)
        .sort(
          (x, y) =>
            y.a.progress!.have / y.a.progress!.need - x.a.progress!.have / x.a.progress!.need,
        )
        .slice(0, 2);
      const tell = prefs?.achievements?.tellLeagues ?? true;
      body = (
        <Stack gap={4}>
          {achievements.snapshot.state === 'failed' && wall === null ? (
            <Callout tone="bad">
              <Stack gap={2}>
                <span>{achievements.snapshot.failure.message}</span>
                <Line>
                  <Button size="xs" onClick={achievements.reload}>
                    Try again
                  </Button>
                </Line>
              </Stack>
            </Callout>
          ) : wall === null ? (
            <Text block>Reading your achievements…</Text>
          ) : (
            <Stack gap={2}>
              {wall.summary.found === 0 ? (
                <Text block>Nothing found yet. The wall says what to try.</Text>
              ) : counts ? (
                <Text block>{wall.summary.found} found, once each, across every league.</Text>
              ) : null}
              {found.length > 0 ? (
                <div className="fsp-ach-grid" data-cols={3}>
                  {found.map((a) => (
                    <FoundTile
                      key={a.id}
                      a={a}
                      family={a.family}
                      onOpen={() =>
                        navigate(`/account/achievements?badge=${encodeURIComponent(a.id)}`)
                      }
                    />
                  ))}
                </div>
              ) : null}
              {counts && closest.length > 0 ? (
                <Stack gap={1}>
                  <Caption>Closest</Caption>
                  <div className="fsp-ach-grid" data-cols={3}>
                    {closest.map(({ a, family }) => (
                      <OpenTile
                        key={a.id}
                        a={a}
                        family={family}
                        onOpen={() =>
                          navigate(`/account/achievements?badge=${encodeURIComponent(a.id)}`)
                        }
                      />
                    ))}
                  </div>
                </Stack>
              ) : null}
            </Stack>
          )}
          <Toggle
            label="Tell my leagues when I find one"
            description="One line in that league’s chat, for what you find in its games, when the league posts them too."
            on={tell}
            onChange={(on) => void saveAchievements({ tellLeagues: on })}
          />
          <Line>
            <Button icon="achievements" onClick={() => navigate('/account/achievements')}>
              See them all
            </Button>
          </Line>
        </Stack>
      );
      break;
    }
    case 'chat': {
      // B-299: the bots' lines, for me alone, on every device. B-448: strong
      // language as written, for me alone, masked until I choose. The two are
      // saved together, the way the account keeps them.
      const shown = !(prefs?.chat?.hideBots ?? false);
      const asWritten = prefs?.chat?.asWritten ?? false;
      const chat = { hideBots: !shown, asWritten };
      body = (
        <Stack gap={4}>
          <Toggle
            label="Show the bots’ lines"
            description="Now and then a bot says something in a league’s chat about a week worth talking about. Off, you see none of it, in any league."
            on={shown}
            onChange={(on) => void saveChat({ ...chat, hideBots: !on })}
          />
          <Toggle
            label="Show messages as written"
            description="Strong language in a league’s chat shows masked, like s••t, until this is on. It changes your chat alone, on every device."
            on={asWritten}
            onChange={(on) => void saveChat({ ...chat, asWritten: on })}
          />
        </Stack>
      );
      break;
    }
    case 'signin':
      body = (
        <Stack gap={4}>
          <Card padded>
            <Line spread>
              <Stack gap={0}>
                <Text weight="strong" size="md">
                  Email
                </Text>
                <Text tone="secondary" nowrap block>
                  {session.email}
                </Text>
                {help('For signing in and league mail.')}
              </Stack>
              <Button size="xs" onClick={() => setEmailOpen(true)}>
                Change
              </Button>
            </Line>
          </Card>
          {/* A CHANGE WAITING ON ITS LINK (B-548): where it went, and a way to take it back. */}
          {session.emailChangingTo ? (
            <Callout tone="warn">
              <Stack gap={2}>
                <span>
                  Waiting for you to confirm <b>{session.emailChangingTo}</b>: the link went there.
                  Until you use it, you sign in with {session.email}.
                </span>
                <Line>
                  <Button
                    size="xs"
                    onClick={() => {
                      void cancelEmailChange().then((o) =>
                        tell(o.ok ? 'The change is taken back.' : o.message, o.ok ? 'good' : 'bad'),
                      );
                    }}
                  >
                    Cancel the change
                  </Button>
                </Line>
              </Stack>
            </Callout>
          ) : null}
          {help(
            'Changing it keeps everything — leagues, history, trophies — because the id owns them, not the address.',
          )}
          <Card padded>
            <Line spread>
              <Stack gap={0}>
                <Text weight="strong" size="md">
                  Password
                </Text>
                {help(PASSWORD_RULE)}
              </Stack>
              <Button
                size="xs"
                onClick={() => {
                  // A fresh list, so the box counts the devices signed in now
                  // rather than when this page opened.
                  devices.reload();
                  setPasswordOpen(true);
                }}
              >
                Change
              </Button>
            </Line>
          </Card>
          <Stack gap={1}>
            <Caption>Ways to sign in</Caption>
            <Card padded>
              <Line spread>
                <div className="fsp-grow">
                  <Text weight="strong" size="md" block>
                    Password
                  </Text>
                  <Text tone="secondary" nowrap block>
                    {session.email}
                  </Text>
                </div>
                <Hint label="An account with no password comes later, so it can’t be removed yet.">
                  <Button size="xs" variant="quiet" disabled>
                    Remove
                  </Button>
                </Hint>
              </Line>
            </Card>
            {/* A CODE BY EMAIL (B-547): a way in for any account with an
                address, asked for at sign-in, so nothing here to turn on. */}
            <Card padded>
              <div className="fsp-grow">
                <Text weight="strong" size="md" block>
                  A code by email
                </Text>
                <Text tone="secondary" nowrap block>
                  {session.email}
                </Text>
                {help(
                  'At sign-in, choose Email me a code instead: six digits and a link, good for 15 minutes.',
                )}
              </div>
            </Card>
            {help('Google or Apple sign-in come later. Each is one more row here.')}
          </Stack>
        </Stack>
      );
      break;
    case 'devices':
      body = (
        <Stack gap={4}>
          {devices.snapshot.state === 'failed' ? (
            <Callout tone="bad">
              <Stack gap={2}>
                <span>{devices.snapshot.failure.message}</span>
                <Line>
                  <Button size="xs" onClick={devices.reload}>
                    Try again
                  </Button>
                </Line>
              </Stack>
            </Callout>
          ) : null}
          {/* A list we never got has no count: "Signed in on 0 devices" beside
              the failure was the claim the comment above forbids (B-133's
              review, 2026-09-24). */}
          {rows === null && devices.snapshot.state === 'failed' ? null : (
            <Text block>
              {devices.snapshot.state === 'loading'
                ? 'Counting your devices…'
                : `Signed in on ${rows?.length ?? 0} device${rows?.length === 1 ? '' : 's'}.`}
            </Text>
          )}
          {(rows ?? []).map((d) => (
            <Card key={d.id} padded>
              <Line spread>
                <div className="fsp-grow">
                  <Text weight="strong" size="md" block>
                    {d.device ?? 'Unknown device'}
                  </Text>
                  <Text tone="secondary" block>
                    {d.current ? 'This device' : 'Signed in'} · since {since(d.createdAt)}
                  </Text>
                </div>
                <Button
                  size="xs"
                  onClick={() => {
                    void (async () => {
                      // Revoking THIS device is a sign-out, and the server
                      // clears the cookie either way — so the app has to let
                      // go of the session too, or it shows a name for an
                      // account it can no longer act as.
                      if (d.current) {
                        await signOut();
                        navigate('/');
                        return;
                      }
                      const left = await revokeDevice(d.id);
                      if (left.status === 'ok') {
                        // The endpoint answers with what is left, and the
                        // reader owns the list, so this re-reads rather than
                        // holding a second copy that could disagree with it.
                        devices.reload();
                        return;
                      }
                      tell(
                        messageOf(
                          left,
                          'That device is still signed in.',
                          'Could not reach the site. Nothing changed.',
                        ),
                        'bad',
                      );
                    })();
                  }}
                >
                  {d.current ? 'Sign out' : 'Revoke'}
                </Button>
              </Line>
            </Card>
          ))}
          {help(
            'Every device that has signed in, with its own sign-out. Revoking one takes effect on its next request.',
          )}
        </Stack>
      );
      break;
    case 'watching':
      // THE WATCHLIST (E5): on the account, so the same stars in every
      // league of the sport and on every device. Unstarred here or by the
      // star beside a name; a mistaken tap here has an Undo, which stars him
      // again from now.
      body = (
        <Stack gap={4}>
          {watchlist.failure !== null ? (
            <Callout tone="bad">
              <Stack gap={2}>
                <span>{watchlist.failure}</span>
                <Line>
                  <Button size="xs" onClick={watchlist.retry}>
                    Try again
                  </Button>
                </Line>
              </Stack>
            </Callout>
          ) : watchlist.players === null ? (
            <Text block>Reading your watchlist…</Text>
          ) : watchlist.players.length === 0 ? (
            <Text block>
              Nobody yet. Tap the star by a player’s name in a league to keep an eye on him.
            </Text>
          ) : (
            <Text block>
              Watching {watchlist.players.length} player
              {watchlist.players.length === 1 ? '' : 's'}.
            </Text>
          )}
          {(watchlist.players ?? []).map((w) => {
            return (
              <Card key={w.player.id} padded>
                <Line spread>
                  <Line gap={2}>
                    {w.player.positions[0] ? (
                      <PositionPill pos={w.player.positions[0]} size="sm" />
                    ) : null}
                    <div className="fsp-grow">
                      <Text weight="strong" size="md" block>
                        {w.player.name}
                      </Text>
                      <Line gap={1}>
                        <ProTeamCode code={w.player.proTeam} />
                        <Text tone="secondary">
                          {w.player.injury ? `${w.player.injury.name} · ` : ''}since{' '}
                          {since(w.addedAt)}
                        </Text>
                      </Line>
                    </div>
                  </Line>
                  <Button
                    size="xs"
                    onClick={() => {
                      const toggle = watchlist.toggle;
                      if (toggle === null) return;
                      void toggle(w.player.id, false).then((refused) => {
                        // A refusal is already said, in the server's words.
                        if (refused !== null) return;
                        tellWithUndo(`Took the star off ${w.player.name}.`, () =>
                          toggle(w.player.id, true),
                        );
                      });
                    }}
                  >
                    Unstar<span className="fsp-sr"> {w.player.name}</span>
                  </Button>
                </Line>
              </Card>
            );
          })}
          {help(
            'The players you star, kept on your account: the same stars in every league of the sport and on every device. Nobody else sees them.',
          )}
        </Stack>
      );
      break;
    case 'data':
      body = (
        <Stack gap={5}>
          <Stack gap={2}>
            <Text weight="strong" size="md" block>
              What we keep about you
            </Text>
            <Text tone="secondary" block>
              An id, your display name and mark, your email, a password hash, which devices are
              signed in, your teams in each league, your choices of how the site looks and what it
              tells you, the players you star, and a log of what you have done here — signed in,
              saved a lineup, changed a look. Nothing else, on purpose.
            </Text>
            <Line>
              <Button icon="download" onClick={exportData}>
                Download as JSON
              </Button>
            </Line>
          </Stack>
          <Card padded tone="bad">
            <Stack gap={2}>
              <Text weight="strong" size="md" block>
                Delete my account
              </Text>
              <Text tone="secondary" block>
                Immediate and real. Everything that is yours goes, what you said in your leagues
                included; your teams stay in their leagues, and their record shows a former manager.
              </Text>
              <Line>
                <Button variant="danger-quiet" icon="delete" onClick={() => setDeleteOpen(true)}>
                  Delete…
                </Button>
              </Line>
            </Stack>
          </Card>
        </Stack>
      );
      break;
  }

  // The faces on my teams, to start from.
  const teamFaces: StartingFace[] = cards.flatMap((c) =>
    c.myTeam?.badge?.kind === 'face' ? [{ name: c.myTeam.name, face: c.myTeam.badge.face }] : [],
  );

  return (
    <>
      <FaceMaker
        opened={making}
        start={session.avatarFace?.face ?? null}
        colors={
          session.avatarFace?.colors ?? {
            primary: avatarHex(session.avatarSeed),
            secondary: '#ffffff',
          }
        }
        teamName={session.displayName}
        others={teamFaces}
        keptOn="your account"
        othersLabel="Your teams"
        onDone={(face, colors) => {
          setMaking(false);
          // A color chosen and not yet saved goes with the face, kept for the initial.
          void update({ avatarFace: { face, colors }, avatarSeed: draft.avatarSeed }).then((o) =>
            tell(o.ok ? 'Your face is your mark now.' : o.message, o.ok ? 'good' : 'bad'),
          );
        }}
        onClose={() => setMaking(false)}
      />
      <FormScreen
        header={
          <Line>
            <Avatar session={session} size="md" />
            <div className="fsp-grow">
              <Title>Your account</Title>
              <div className="fsp-sub fsp-ui-wrap-text">
                Everything here can change. Your leagues stay yours.
              </div>
            </div>
          </Line>
        }
        sections={sections}
        active={active}
        onSection={(id) => setActive(id as SectionId)}
        content={body}
        dirtyLabel={changes ? `${changes} ${changes === 1 ? 'change' : 'changes'}` : null}
        onDiscard={discard}
        onSave={save}
        saveDisabled={!nameOk || saving}
      />
      <ChangeEmailSheet opened={emailOpen} onClose={() => setEmailOpen(false)} />
      <ChangePasswordSheet
        opened={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        // Null while the list is not in hand: the sheet then asks about
        // "my other devices" without a number it does not have.
        others={rows === null ? null : rows.filter((d) => !d.current).length}
        onChanged={() => devices.reload()}
      />
      <DeleteAccountSheet
        opened={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        email={session.email === '' ? null : session.email}
        onDownload={exportData}
        onDeleted={() => {
          setDeleteOpen(false);
          navigate('/');
          tell('Your account is gone. Thanks for playing.');
        }}
      />
    </>
  );
}
